import type { FC, MouseEventHandler, ReactNode } from 'react'

import { ArrowRight, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PageLink } from '@/components/kit/page-link'

type ButtonVariant = 'primary' | 'dark' | 'light' | 'outline'

type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps {
    children: ReactNode
    href?: string
    onClick?: MouseEventHandler<HTMLElement>
    variant?: ButtonVariant
    size?: ButtonSize
    icon?: 'arrow' | 'play' | 'none'
    type?: 'button' | 'submit'
    disabled?: boolean
    className?: string
    cursor?: string
    external?: boolean
    ariaLabel?: string
}

const variants: Record<ButtonVariant, { base: string, chip: string }> = {
    primary: {
        base: 'bg-ember text-ink hover:bg-[#ff6a14] shadow-[0_14px_34px_-16px_rgb(255_92_0/0.8)]',
        chip: 'bg-ink text-cream'
    },
    dark: {
        base: 'bg-ink text-cream hover:bg-black',
        chip: 'bg-ember text-ink'
    },
    light: {
        base: 'bg-white text-ink hover:bg-paper shadow-[0_14px_34px_-18px_rgb(0_0_0/0.4)]',
        chip: 'bg-ink text-cream'
    },
    outline: {
        base: 'border border-ink/15 bg-white/30 text-ink hover:border-ink/40 hover:bg-white/70',
        chip: 'bg-ink text-cream'
    }
}

const sizes: Record<ButtonSize, { base: string, chip: string, solo: string }> = {
    sm: { base: 'h-10 gap-2.5 text-sm', chip: 'size-7', solo: 'px-4' },
    md: { base: 'h-12 gap-3 text-[15px]', chip: 'size-9', solo: 'px-6' },
    lg: { base: 'h-14 gap-4 text-base', chip: 'size-10', solo: 'px-7' }
}

const padding: Record<ButtonSize, { arrow: string, play: string }> = {
    sm: { arrow: 'pl-4 pr-1.5', play: 'pl-1.5 pr-4' },
    md: { arrow: 'pl-5 pr-1.5', play: 'pl-1.5 pr-5' },
    lg: { arrow: 'pl-6 pr-2', play: 'pl-2 pr-6' }
}

export const Button: FC<ButtonProps> = ({
    children,
    href,
    onClick,
    variant = 'primary',
    size = 'md',
    icon = 'arrow',
    type = 'button',
    disabled = false,
    className,
    cursor,
    external = false,
    ariaLabel
}) => {
    const classes = cn(
        'group/btn relative inline-flex shrink-0 items-center justify-center rounded-full font-medium tracking-tight whitespace-nowrap transition-[background-color,border-color,box-shadow,transform,color] duration-300 ease-out active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60',
        variants[variant].base,
        sizes[size].base,
        icon === 'none' ? sizes[size].solo : padding[size][icon],
        className
    )

    const chip = icon !== 'none' && (
        <span className={cn('relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full', variants[variant].chip, sizes[size].chip)}>
            {icon === 'play' ? (
                <Play className='size-3.5 translate-x-px fill-current transition-transform duration-500 ease-expo group-hover/btn:scale-125' />
            ) : (
                <>
                    <ArrowRight className='size-4 transition-transform duration-500 ease-expo group-hover/btn:translate-x-[220%]' />
                    <ArrowRight className='absolute size-4 -translate-x-[220%] transition-transform duration-500 ease-expo group-hover/btn:translate-x-0' />
                </>
            )}
        </span>
    )

    const content = (
        <>
            {icon === 'play' && chip}
            <span className='relative'>
                {children}
            </span>
            {icon === 'arrow' && chip}
        </>
    )

    if (href) {
        const isNative = external || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http')

        if (isNative) {
            return (
                <a
                    href={href}
                    onClick={onClick}
                    className={classes}
                    data-cursor={cursor}
                    aria-label={ariaLabel}
                    target={external || href.startsWith('http') ? '_blank' : undefined}
                    rel={external || href.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                    {content}
                </a>
            )
        }

        return (
            <PageLink
                href={href}
                onClick={onClick}
                className={classes}
                data-cursor={cursor}
                aria-label={ariaLabel}
            >
                {content}
            </PageLink>
        )
    }

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={classes}
            data-cursor={cursor}
            aria-label={ariaLabel}
        >
            {content}
        </button>
    )
}
